<script setup lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import { Icons } from '@/models/icons.enum';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  allowedExtensions: string[];
  allowedMimeTypes: string[];
  maxFileSizeBytes: number;
}

const props = defineProps<Props>();
const emit = defineEmits(['onChangeFile', 'onRemoveFile']);
const { t } = useI18n();
const inputRef = ref<HTMLInputElement | null>(null);
const fileName = ref('');
const validationError = ref<FileValidationError | null>(null);

const acceptAttribute = computed(() => [...props.allowedExtensions, ...props.allowedMimeTypes].join(','));

const maxFileSizeLabel = computed(() => {
  const mb = props.maxFileSizeBytes / (1024 * 1024);

  return `${mb}MB`;
});

const errorText = computed(() => {
  if (validationError.value) {
    return validationError.value === FileValidationError.WRONG_FILE_TYPE
      ? t(`excel_order_upload.excel_upload_modal.error.${validationError.value}`)
      : t(`excel_order_upload.excel_upload_modal.error.${validationError.value}`, {
          max_file_size: maxFileSizeLabel,
        });
  }

  return '';
});

enum FileValidationError {
  FILE_SIZE_TOO_BIG = 'file_size_too_big',
  WRONG_FILE_TYPE = 'wrong_file_type',
}

const validateFile = (file: File): FileValidationError | null => {
  const hasValidExtension = props.allowedExtensions.some((ext) => file.name.endsWith(ext));
  const hasValidMime = props.allowedMimeTypes.includes(file.type);

  if (!hasValidExtension || !hasValidMime) {
    return FileValidationError.WRONG_FILE_TYPE;
  }

  if (file.size > props.maxFileSizeBytes) {
    return FileValidationError.FILE_SIZE_TOO_BIG;
  }

  return null;
};

const onChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const file = target.files?.[0];

  if (file) {
    const error = validateFile(file);
    if (error) {
      validationError.value = error;

      return;
    }

    validationError.value = null;
    fileName.value = file.name;
    emit('onChangeFile', file);
  }
};

const onRemoveFile = () => {
  fileName.value = '';
  emit('onRemoveFile');
};

const onClickBrowse = () => {
  inputRef.value?.click();
};
</script>

<template>
  <div class="error-boundary" :class="{ 'error-boundary--error': !!validationError }">
    <section aria-label="file-selection-section" class="file-selection" @dragover.prevent>
      <input
        id="file-upload-input"
        ref="inputRef"
        class="file-selection__input"
        type="file"
        :accept="acceptAttribute"
        v-on:change="onChange"
        @cancel.stop />
      <div class="drop-off-icons">
        <BackstageIcon :name="Icons.DOTTED_CIRCLE" class="drop-off-icons__circle" />
        <BackstageIcon :name="Icons.ARROW_DOWN" class="drop-off-icons__arrow" />
      </div>
      <div class="file-selection__body">
        <div v-if="fileName" class="selected-file">
          <p class="file-selection__text">{{ fileName }}</p>
          <BackstageIcon :name="Icons.TRASH" class="selected-file__trash" @click="onRemoveFile" />
        </div>
        <div v-else-if="validationError">
          <p class="file-selection__text file-selection__text--error">{{ errorText }}</p>
        </div>
        <div v-else class="instructions">
          <p class="file-selection__text">
            {{ t('excel_order_upload.excel_upload_modal.instruction', { max: maxFileSizeLabel }) }}
          </p>
          <p class="file-selection__text">{{ t('excel_order_upload.excel_upload_modal.or') }}</p>
        </div>
      </div>
      <BackstageButton class="file-selection__cta" :full-width="true" @click="onClickBrowse">{{
        t('excel_order_upload.excel_upload_modal.button_label.browse_files')
      }}</BackstageButton>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.error-boundary {
  &--error {
    border: 1px solid var(--border-error-color);
    border-radius: 4px;
  }
}

.file-selection {
  border: 8px solid var(--border-color);
  border-radius: 4px;
  padding: 54px 62px 32px;
  display: flex;
  flex-direction: column;
  row-gap: 32px;

  position: relative;

  &__input {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    opacity: 0;
    padding: 0;
  }

  &__body {
    min-height: 2lh;
  }

  &__text {
    margin: 0;
    font-size: 1rem;

    &--error {
      color: var(--content-negative);
    }
  }

  &__cta {
    z-index: 1;
  }
}

.drop-off-icons {
  display: grid;
  grid-template-columns: 80px;
  grid-template-rows: 80px;
  place-items: center;
  place-content: center;

  &__circle,
  &__arrow {
    grid-column: 1;
    grid-row: 1;
  }

  &__circle {
    padding: 40px;
  }

  &__arrow {
    padding: 20px;
  }
}

.selected-file {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__trash {
    cursor: pointer;
    padding: 12px;
  }
}

.instructions {
  display: flex;
  flex-direction: column;
  row-gap: 9px;
}
</style>
